import { Injectable } from '@angular/core';
import { ToggleChatContainerE } from '@enums/attendances.enum';
import { GroupTabsEnum } from '@enums/group-tabs.enum';
import { GroupI, GroupMentionI, MessageGroupI } from '@interfaces/group.interface';
import { ComponentStore } from '@ngrx/component-store';
import { ToggleGroupContainerT } from '@omnitypes/group.type';
import { BehaviorSubject, firstValueFrom, map, Observable, Subject } from 'rxjs';
import { GroupService } from '@modules/chat/group.service';
import * as moment from 'moment';
import { ContactI } from '@interfaces/contact.interface';
import { isString } from 'lodash';
import { MessageGroupStoreService } from './message-group-store.service';
import { MessageStatusE, SenderTypeE } from '@enums/message.enum';
import { PageI } from '@interfaces/page.interface';
import { UserHelperService } from '@helpers/user-helper.service';
import { ChannelE } from '@interfaces/channel.interface';
import { linkToGlobalState } from '@helpers/redux-devtools.helper';
import { Store } from '@ngrx/store';

export interface GroupsState {
  groups: GroupI[];
  groupsForward: GroupI[];
  currentGroupId: string;
  groupToggle: ToggleGroupContainerT;
  currentChannelId: string;
  groupSelected: boolean | null;
  syncGroup: boolean | null;
  contactAttendance: ContactI | null;
  forwardingMessage: MessageGroupI | null;
  groupFiltered: { [type: string]: GroupI[] };
}

@Injectable({
  providedIn: 'root',
})
export class GroupStoreService extends ComponentStore<GroupsState> {
  public selectedMessages: string[] = [];
  public completeSelectedMessages: MessageGroupI[] = [];

  constructor(
    private readonly groupService: GroupService,
    private messageGroupStoreService: MessageGroupStoreService,
    private readonly userHelperService: UserHelperService,
    private store: Store
  ) {
    super(GroupStoreService.initialState());
    linkToGlobalState(this.state$, 'group', this.store);
    UserHelperService.logout$.subscribe((logouted: boolean) => {
      if (logouted) {
        this.clearAll();
      }
    });
  }

  public groups$: Observable<GroupI[]> = this.select((state) => state.groups);
  public groupsForward$: Observable<GroupI[]> = this.select((state) => state.groupsForward);
  public groupFiltered$: Observable<{ [type: string]: GroupI[] }> = this.select((state) => state.groupFiltered);
  public currentChannelId$: Observable<string> = this.select((state) => state.currentChannelId);
  public syncGroup$: Observable<boolean | null> = this.select((state) => state.syncGroup);
  public groupToggle$: Observable<ToggleGroupContainerT> = this.select((state) => state.groupToggle);
  public forwardingMessage$: Observable<MessageGroupI | null> = this.select((state) => state.forwardingMessage);
  private readGroupStatus: BehaviorSubject<{ [id: string]: string }> = new BehaviorSubject<{ [id: string]: string }>(
    {}
  );
  public hideButtonContainer: boolean = false;

  private refreshGroupSubject: BehaviorSubject<any> = new BehaviorSubject(null);

  private groupTabSubject = new Subject<GroupTabsEnum | null>();
  public groupTabChanged$ = this.groupTabSubject.asObservable();

  private newInternalGroupSubject = new Subject<GroupI>();
  public newInternalGroup$ = this.newInternalGroupSubject.asObservable();

  public openGroupTab(tab: GroupTabsEnum | null): void {
    this.groupTabSubject.next(tab);
  }

  public clearAll() {
    this.setState((state) => GroupStoreService.initialState());
  }
  public static initialState() {
    return {
      groups: [],
      groupsForward: [],
      groupToggle: ToggleChatContainerE.DEFAULT,
      currentGroupId: '',
      currentChannelId: '',
      groupSelected: false,
      syncGroup: false,
      contactAttendance: null,
      forwardingMessage: null,
      groupFiltered: {
        all: [],
      },
    };
  }

  public currentGroup$: Observable<GroupI | undefined> = this.select((state) => {
    if (!state.currentGroupId) return undefined;
    return state.groups.find((group) => group._id === state.currentGroupId);
  });

  public changeGroupSelected(): void {
    this.hideButtonContainer = false;
    this.setState((state) => ({ ...state, groupSelected: !state.groupSelected } as GroupsState));
  }

  public setGroupType(type: ToggleGroupContainerT): void {
    this.setState((state) => ({ ...state, groupToggle: type } as GroupsState));
  }

  public setNullGroupSelected(): void {
    this.hideButtonContainer = false;
    this.setState((state) => ({ ...state, groupSelected: null } as GroupsState));
  }

  public updateUnsentMessage(groupId: string, message: string): void {
    try {
      this.updateAttendanceUnsentMessage(groupId, message);
    } catch (error) {
      throw new Error(`GroupStoreService updateMessageStatus error: ${error}`);
    }
  }

  public setSyncGroup(syncing: boolean): void {
    this.setState((state) => ({
      ...state,
      syncGroup: syncing,
    }));
  }

  public setCurrentGroupId(groupId: string): void {
    this.setState(
      (state) =>
        ({
          ...state,
          currentGroupId: groupId,
        } as GroupsState)
    );
  }

  public populateStore(groups: GroupI[]): void {
    groups.map(async (group) => {
      if (!group._id) return;

      void firstValueFrom(
        this.checkIfGroupIdExists(group._id, this.groups$).pipe(
          map((exists) => {
            if (!exists) {
              this.setState((state) => ({ ...state, groups: [...state.groups, group] } as GroupsState));
            } else {
              this.setState((state) => {
                if (state.groups && state.groups.length > 0) {
                  state.groups = state.groups.map((stateGroup) =>
                    stateGroup._id === group._id ? { ...stateGroup, ...group } : stateGroup
                  );
                }

                return state;
              });
            }

            this.populateMessageFromLastMessage(group);
            this.updateReadStatus(group._id, group?.readStatus ?? 'read');
          })
        )
      );
    });
  }

  public populateGroupsFilter(type: string, chats: GroupI[], clean: boolean = false): void {
    this.setState((state) => {
      if (clean) {
        state.groupFiltered[type] = [];
      }

      state.groupFiltered[type] = state.groupFiltered[type].concat(chats);

      return { ...state };
    });
  }

  public removeGroupsFilter(type: string): void {
    this.setState((state) => {
      state.groupFiltered[type] = [];

      return { ...state };
    });
  }

  private async populateMessageFromLastMessage(group) {
    this.messageGroupStoreService.populateStore(
      group._id,
      {
        data: group.latestMessage ? [group.latestMessage] : [],
        meta: { page: 1, limit: 50, itemCount: 0, hasPreviousPage: false, hasNextPage: true },
      } as PageI<MessageGroupI>,
      true
    );
  }

  public removeGroup(groupId: string) {
    this.setState(
      (state) =>
        ({
          ...state,
          groups: state.groups.filter((group) => group._id !== groupId),
        } as GroupsState)
    );
  }

  public removeAllGroup(channelTypes: ChannelE[]) {
    this.setState((state) => {
      if (state.groups && state.groups.length > 0) {
        state.groups = state.groups.filter((group) => !this.hasChannelTypes(group, channelTypes));
      }

      return state;
    });
  }

  public setChannel(currentChannelId: string): void {
    this.setState((state) => ({ ...state, currentChannelId } as GroupsState));
    const isTypeFilter = ['all', 'whatsapp', 'telegram'].includes(currentChannelId);
    if (!isTypeFilter) {
      void this.groupService.setChannel(currentChannelId);
    }
  }

  private updateAttendanceUnsentMessage(groupId: string, message: string) {
    this.setState(
      (state) =>
        ({
          ...state,
          groups: state.groups.map((group) => {
            if (group._id === groupId) {
              return {
                ...group,
                unsentMessage: message,
              };
            }

            return group;
          }),
        } as GroupsState)
    );
  }

  public addMessageInGroup(groupId: string, message: MessageGroupI): void {
    if (!message) {
      return;
    }
    this.setState(
      (state) =>
        ({
          ...state,
          groups: state.groups.map((group) => {
            if (!group.messages) {
              group.messages = [];
            }
            if (group._id === groupId) {
              if (!group.messages.some((m) => m?._id === message._id)) {
                if (!message?._id) {
                  return { ...group };
                }
                if (state.currentGroupId === groupId && message?.sender?.type === SenderTypeE.CONTACT) {
                  message.status = MessageStatusE.READ;
                }
                group.messages.push(message);
                group.messages = group.messages.filter((m) => !isString(m));
                group.messages = group.messages.sort((a, b) => {
                  return moment(a.time).diff(moment(b.time), 'seconds');
                });
                group.latestMessage = group.messages[group.messages.length - 1];
              }
              return { ...group };
            }
            return group;
          }),
        } as GroupsState)
    );
  }

  addLastMessage(groupId: string, message: MessageGroupI) {
    this.setState(
      (state) =>
        ({
          ...state,
          groups: state.groups.map((group) => {
            if (group._id === groupId) {
              group.latestMessage = message;
              return { ...group };
            }
            return group;
          }),
        } as GroupsState)
    );
  }

  private checkIfGroupIdExists(groupId: string, someGroup$: Observable<GroupI[]>): Observable<boolean> {
    return someGroup$.pipe(map((groups) => groups.some((group) => group._id === groupId)));
  }

  public filterGroups(
    searchText: string,
    channelTypes: ChannelE[],
    onlyMentions: boolean = false
  ): Observable<GroupI[]> {
    return this.groups$.pipe(
      map((groups) =>
        groups.filter((group) => {
          const hasSearchText = this.hasSearchText(group, searchText);
          const hasChannelTypes = this.hasChannelTypes(group, channelTypes);
          const hasOnlyMentions = this.hasOnlyMentions(group, onlyMentions);

          return hasSearchText && hasChannelTypes && hasOnlyMentions;
        })
      )
    );
  }

  private hasSearchText(group: GroupI, searchText: string) {
    if (!searchText) return true;

    return (
      group?.participants?.some((participant) => {
        if (!participant) return false;
        return (
          (participant?.contact as ContactI)?.telephone?.toLowerCase()?.includes(searchText.toLowerCase()) ||
          (participant?.contact as ContactI)?.name?.toLowerCase()?.includes(searchText.toLowerCase()) ||
          (participant?.contact as ContactI)?.email?.toLowerCase()?.includes(searchText.toLowerCase())
        );
      }) ||
      group?.subject?.toLowerCase()?.includes(searchText.toLowerCase()) ||
      group?.desc?.toLowerCase()?.includes(searchText.toLowerCase())
    );
  }

  private hasChannelTypes(group: GroupI, channelTypes: ChannelE[]) {
    if (!(channelTypes && channelTypes.length > 0)) return true;

    return channelTypes.includes(group.channel.type);
  }

  private hasOnlyMentions(group: GroupI, onlyMentions: boolean = false) {
    if (!onlyMentions) return true;

    if (group.mentions && group.mentions.length > 0) {
      const userId = this.userHelperService.getUserAttributes()['custom:userId'];

      return group.mentions.find((mention) => mention.agent?._id === userId);
    }

    return false;
  }

  public changeSelectdeMessages(newMessages: string[], newCompleteMessages: MessageGroupI[]): void {
    this.selectedMessages = newMessages;
    this.completeSelectedMessages = newCompleteMessages;
  }

  setForwardMessage(message: MessageGroupI | null) {
    this.setState((state) => ({
      ...state,
      forwardingMessage: message,
    }));
  }

  public getReadGroupStatusById(groupId: string): Observable<string> {
    return this.readGroupStatus.asObservable().pipe(map((readChatStatus) => readChatStatus[groupId]));
  }

  updateReadStatus(id: string, status: string) {
    const readChatStatus = this.readGroupStatus.getValue();
    readChatStatus[id] = status;
    this.readGroupStatus.next(readChatStatus);
  }

  public addNewGroupForward(group: GroupI): void {
    try {
      this.setState((state) => {
        const inArray = state.groupsForward.map((state_contact) => state_contact._id).includes(group._id);
        if (!inArray) {
          const groupsForward = [...state.groupsForward, group];
          return { ...state, groupsForward };
        }
        return state;
      });
    } catch (error) {
      throw new Error(`ContactStoreService addNewContact error: ${error}`);
    }
  }

  public getRefreshGroup(): Observable<any> {
    return this.refreshGroupSubject.asObservable();
  }

  public refreshGroup() {
    this.refreshGroupSubject.next(null);
  }

  public newInternalGroup(group: GroupI) {
    this.newInternalGroupSubject.next(group);
  }

  public async checkGroupExistsInStore(groupId: string): Promise<boolean> {
    return await firstValueFrom(this.checkIfGroupIdExists(groupId, this.groups$));
  }

  public getGroupById(groupId: string): Observable<GroupI | undefined> {
    return this.select((state) => {
      return state.groups.find((group) => group._id === groupId);
    });
  }

  public async getMentionsInGroup(groupId: string): Promise<GroupMentionI[]> {
    const group = await firstValueFrom(this.getGroupById(groupId));

    if (group && group.mentions) {
      return group.mentions;
    }

    return [];
  }

  public updateMentionsInGroup(groupId: string, groupMentions: GroupMentionI[] | undefined) {
    this.setState((state) => ({
      ...state,
      groups: state.groups.map((group) => {
        if (group._id === groupId) {
          return {
            ...group,
            mentions: groupMentions,
          };
        }
        return group;
      }),
    }));
  }
}
